/* eslint-disable max-classes-per-file */
import { inject, Injectable } from '@angular/core';
import { Router, UrlTree } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { Jwt } from 'src/app/core/misc/jwt';
import { AppState } from '../state/appState';
import { AuthActions } from '../state/auth/auth.actions';
import { LoginProviderType } from '../state/types';
import { UserSettingsStorage } from '../storage/user.settings.storage';

interface AuthGuardProvider {
  isLoginProviderActive: () => boolean;
  validate: () => boolean | string;
  simpleValidate: () => boolean | string;
}

class LegacyAuthGuardProvider implements AuthGuardProvider {
  private uss = new UserSettingsStorage();

  isLoginProviderActive(): boolean {
    return this.uss.loadSetting(UserSettingsStorage.LOGIN_PROVIDER) === LoginProviderType.LEGACY;
  }

  validate(): boolean | string {
    const jwt = this.uss.loadSetting('jwt');

    if (!jwt) {
      // missing token, go to login without error
      return false;
    }

    if (!Jwt.isValidToken(jwt, true)) {
      this.uss.removeSetting('jwt');
      return 'INVALID_TOKEN';
    }
    return true;
  }

  simpleValidate(): boolean | string {
    const jwt = this.uss.loadSetting('jwt');
    return !!jwt;
  }
}

class SsoAuthGuardProvider implements AuthGuardProvider {
  private uss = new UserSettingsStorage();

  isLoginProviderActive(): boolean {
    return this.uss.loadSetting(UserSettingsStorage.LOGIN_PROVIDER) === LoginProviderType.SSO;
  }

  validate(): boolean | string {
    const token = this.uss.loadSetting(UserSettingsStorage.CSRF_TOKEN);
    return !!token;
  }

  simpleValidate(): boolean | string {
    const token = this.uss.loadSetting(UserSettingsStorage.CSRF_TOKEN);
    return !!token;
  }
}

class EmptyAuthGuardProvider implements AuthGuardProvider {
  isLoginProviderActive(): boolean {
    return false;
  }

  validate(): boolean | string {
    return false;
  }

  simpleValidate(): boolean | string {
    return false;
  }
}

@Injectable({
  providedIn: 'root',
})
export class AuthGuard {
  private store = inject(Store<AppState>);
  private router = inject(Router);
  private providers = [new LegacyAuthGuardProvider(), new SsoAuthGuardProvider()];

  // _route: ActivatedRouteSnapshot,
  // _state: RouterStateSnapshot,
  canActivate(): boolean | UrlTree | Observable<boolean | UrlTree> | Promise<boolean | UrlTree> {
    const provider = this.findActiveProvider();
    const validationResult = provider.validate();

    if (validationResult !== true) {
      const params = typeof validationResult === 'string' ? validationResult : '';
      return this.router.parseUrl(`/login${params ? '?error=' + params : ''}`);
    }

    // trigger loading of current user from backend if token is valid and not loaded before
    this.store.dispatch(AuthActions.reloadCurrentAuthUserIfNotLoaded());

    return validationResult;
  }

  // _childRoute: ActivatedRouteSnapshot,
  // _state: RouterStateSnapshot,
  canActivateChild(): boolean | UrlTree | Observable<boolean | UrlTree> | Promise<boolean | UrlTree> {
    const provider = this.findActiveProvider();
    const result = provider.simpleValidate();
    return result === true ? true : this.router.parseUrl('/login?error=MISSING_TOKEN');
  }

  private findActiveProvider(): AuthGuardProvider {
    return this.providers.find((p) => p.isLoginProviderActive()) || new EmptyAuthGuardProvider();
  }
}
